(ns com.theplantscape.plant-web.components.tiles
  (:require
   [clojure.string :as str]
   [reagent.core :as r]
   [reitit.frontend.easy :as rfe]
   [com.theplantscape.plant-web.components.images :refer [profile-image default-image build-image-path build-raw-image-path]]
   [com.theplantscape.local-store.ensemble
    :as db
    :refer [fetch-plant fetch-plant-related set-colour]]
   [com.theplantscape.plant-web.draw.events :refer [select-ob-by-entity]]
   [com.theplantscape.plant-web.draw.constants :refer [do-plant-img]]
   [com.theplantscape.web-ui.tailwind.ensemble :refer [colour-picker simple-list ui-btn-action]]
   [com.theplantscape.web-ui.tailwind.modal :refer [image-modal]]))

(def image-path (partial build-image-path do-plant-img))
(def raw-image-path (partial build-raw-image-path do-plant-img))

(defn fa-icon [icon]
  [:i.text-6xl.fa-solid {:class icon}])

(defn ui-simple-icon [{:keys [title icon alt href description]}]
  [:a {:href href :title alt}
   [:div.relative.flex.flex-col.items-center.p-6.bg-white.rounded.shadow-xl
    [:div.text-center.w-16.h-16.p-1.-mt-1.mb-2 [:i.text-6xl.fa-solid {:class icon}]]
    [:h4.text-xl.font-bold.leading-snug.tracking-tight.mb-1 title]
    [:p.text-gray-600.text-center description]]])

(defn detail-icons [key]
  (case key
    :plant/common-name [:i.fa-solid.fa-sign-hanging.mr-2]
    :plant/scientific-name [:i.fa-solid.fa-microscope.mr-2]
    :plant/height [:i.fa-solid.fa-ruler-vertical.mr-2]
    :plant/hardiness [:i.fa-solid.fa-temperature-half.mr-2]
    :plant/family [:i.fa-solid.fa-family.mr-2]
    :plant/lifecycle [:i.fa-solid.fa-tire.mr-2]
    :plant/type [:i.fa-solid.fa-layer-group.mr-2]
    [:i.fa-solid.fa-hashtag]))

(defn simple-tile-container [tiles]
  [:div.py-16.md:py-24.bg-brand-cream
   [:div.container.mx-auto.px-4.sm:px-6.lg:px-8
    (into [:div.grid.sm:grid-cols-2.lg:grid-cols-4.gap-8.mt-12] tiles)]]
  )

(defn simple-tile [{:keys [title image description link] :as vals}]
  [:div.p-6.rounded-xl.shadow-lg.bg-white.hover:shadow-2xl.transition-shadow.duration-300
   [:a {:href link}
    [:div.flex.justify-center.mb-6 image]
    [:h3.text-2xl.font-semibold.font-serif.text-brand-green-dark.mb-3.text-center title]
    [:p.text-gray-600.text-center description]]]
  )

(defn tile [{:keys [link title image] :as vals}]
  [:article.br4.overflow-hidden.ba.b--black-10.justify-centre.content-center
   [:a.justify-centre.content-center.button
    {:href link :on-click (:on-click vals)}
    [:img.h-48.object-cover
     {:src (profile-image image "medium")}]]
   [:div.p0.ma2
    (when (:remove-fn vals)
      [:span.pb2 {:on-click (:remove-fn vals)}
       [:i.db.fas.fa-minus-circle]])
    [:span.pb2 title]]])

(defn ui-plant-calendar [items]
  (when (seq items)
    [:table.w-full.m-4 {:cell-spacing "0"}
     [:thead
      [:tr.stripe-dark [:td.ttc "type"] [:td.ttc "Start"] [:td.ttc "End"]]]
     [:tbody.lh-copy
      (map-indexed
       (fn [idx [key value]]
         (when value
           [:tr.stripe-dark {:key idx}
            [:td.ttc key]
            [:td.ttc (:start value)]
            [:td.ttc (:end value)]])) items)]]))

(defn ui-plant-details [items]
  (when (seq items)
    (simple-list items
                 (fn [[key value]]
                   [:div.w-full.flex.flex-column
                    [:div {:class "w-1/2"}
                     [detail-icons key]
                     key]
                    [:div {:class "w-1/2"} value]]))

    #_(into [:ul.list-none.bg-gray-300.m-4]
            (mapv
             (fn [[key value]]
               [:li.w-full
                {:key key}
                [:div.flex.flex-column
                 [:div {:class "w-1/2"} key]
                 [:div {:class "w-1/2"} value]]])
             items)))

  #_(when (seq items)
      (into [:ul.list-none.bg-gray-300.m-4]
            (mapv
             (fn [[key value]]
               [:li.w-full
                {:key key}
                [:div.flex.flex-column
                 [:div {:class "w-1/2"} key]
                 [:div {:class "w-1/2"} value]]])
             items))))

(defn ui-image-set [images]
  (map-indexed (fn [idx img]
                 [:img.h5 {:key idx :src (str "http://localhost:3000/images/" img) :alt "Missing"}]) images))

(defn paginate-forward [current items]
  (when (seq items)
    (cond (= (+ 1 (.indexOf items current)) (count items)) (first items)
          :else (nth items (+ 1 (.indexOf items current))))))

(defn paginate-backward [current items]
  (when (seq items)
    (cond (= 0 (.indexOf items current)) (last items)
          :else (nth items (- (.indexOf items current) 1)))))

(defn entity-tile
  [entity-id
   plant-entity-id
   {:keys [remove-fn move-fn info-fn set-colour-fn info-toggle switch-plant-fn]}]
  (let [{:keys [:db/id :plant/common-name :plant/type :plant/scientific-name :plant/description :plant/link :plant/image]
         :as plant}
        (fetch-plant plant-entity-id)
        show-fill-picker (r/atom false)
        show-color-picker (r/atom false)]
    (fn [entity-id plant-entity-id {:keys [info-toggle]}]
      (let [plant (r/atom (fetch-plant-related plant-entity-id))]
        [:article.p-2.w-full
         [:div.flex.flex-col.w-full
          [:div {:class "w-full"}
           [ui-btn-action "Remove" remove-fn]
           [ui-btn-action "Move" move-fn]
           [ui-btn-action "Colour" #(swap! show-color-picker not)]
           [ui-btn-action "switch" switch-plant-fn]
           ;[ui-btn-action "Fill" #(swap! show-fill-picker not)]
           (when (int? plant-entity-id)
             [ui-btn-action "info" info-fn])
           [ui-btn-action "<" #(select-ob-by-entity
                                (paginate-forward
                                 entity-id
                                 (-> @plant :plants)))]
           [ui-btn-action ">" #(select-ob-by-entity
                                (paginate-backward
                                 entity-id
                                 (-> @plant :plants)))]]
          (when @show-color-picker
            [colour-picker (partial set-colour entity-id)])
          (when info-toggle
            [:div.flex.flex-row.pt-2 {:class "w-full"}
             [:div
              ;(str plant-entity-id)
              [:div.text-xl [:span.pb2.tracked.ttc {:title "Common name"} (str/capitalize (name common-name))]]
              [:div.text-xl [:span.pb2.ttc {:title "Scientific/Botanical name"} (str/capitalize (str scientific-name))]]]
             [:div.pl-2
              ;(str plant)
              [:img.w-48 {:src (str (or image (default-image)) "?p=h-192")}]]])]
         [:div.p0.ma2 [:span.pb2 description]]]))))

(defn ui-image [images size]
  (if (vector? images)
    (into [:div] (mapv (fn [image] [:img {:class (or size "h-48") :src (str (image-path image) "?p=" size)}]) images))
    [:img {:class (or size "h-48") :src (str (image-path images) "?p=" (or size "h-48"))}]))

(defn plant-tile
  [{:keys [plant/scientific-name
           plant/common-name
           plant/description
           plant/image
           plant/family
           link title
           plant/type
           :xt/id]
    :or {image nil} :as plant}]
  (let [show-img-modal (r/atom false)]
    (fn [{:keys [plant/scientific-name
                 plant/common-name
                 plant/description
                 plant/image
                 plant/family
                 link title
                 plant/type
                 :xt/id]
          :or {image nil} :as plant}]
      [:article.p-4.w-full
       (when @show-img-modal
         [image-modal
          (str (raw-image-path (first (:plant.images/examples plant))) "?p=h-96")
          (fn [e] (swap! show-img-modal not))])
       [:a {:href (rfe/href :plant-edit {:id id})} "Edit"]
       [:div {:on-click #(swap! show-img-modal not)}
        [:div.text-4xl [:span.pb2.tracked.ttc {:title "Common name"} (str/capitalize (str common-name))]]
        [:div.text-4xl [:span.pb2.ttc {:title "Scientific/Botanical name"} (str/capitalize (str scientific-name))]]
        [ui-image (:plant.images/examples plant) "h-96"]
        [:div.p0.ma2 [:span.pb2 description]]]
       [:div (->> plant
                  (filter (fn [[k v]] (set? v)))
                  (map (fn [[k images]] (ui-image-set images))))]

       [:div.flex.flex-column.items-start.w-full
        [:div {:class "w-1/2"}
         [ui-plant-details
          (reduce (fn [m [k v]]
                    (cond
                      ;; don't render maps or sets
                      (map? v) m
                      (set? v) m
                      ;; skip certain keys in a map
                      (str/starts-with? (str k) ":plant.images") (conj m [k (ui-image v "h-24")])
                      (= k :plant/id) m
                      (= k :db/id) m
                      (= k :xt/id) m
                      :else (conj m [k (str v)])))
                  []
                  plant)]]
        [:div {:class "w-1/2"}
         [ui-plant-calendar
          (reduce (fn [m [k v]]
                    (cond (map? v) m
                          (= k :xt/id) m

                          ;; skip maps
                          (conj m [k v])
                          m)) [] plant)]]]])))

